/*
 *  Copyright (c) Qwoach LLC 2020. All rights reserved.
 *  Unauthorized copying of this file, via any medium is strictly prohibited
 *  Proprietary and confidential
 *  Created by Artem Bogush <artem@qwoach.com> on 30/9/2020
 */
import {classToClass} from "@qwoach/class-transformer-wrapper";
import BaseObject from "@shared/BaseObject.js";
import {RoleEnum} from "@/core/js/model/Permissions/RoleEnum.js";
import {logError, userTimezone, userTimezoneName} from "@/utils/helpers.js";
import {clearClientSessionMarker, setClientSessionMarker} from "@/core/js/helpers/ClientSessionMarkerHelper";

import {AuthStateEnum} from "@shared/AuthStateEnum.js";
import {AuthOperationEnum} from "@shared/AuthOperationEnum.js";

export default class AuthManager extends BaseObject {

	auth
	store
	dataManager
	analyticManager
	portalManager = null

	userProfile = null
	activeOperation = null
	refresh = null
	sessionVersion = 0
	authObserver = null
	authCallbacks = new Set()
	claimsUnsubscribe = null
	claimsRetryTimer = null
	sessionWaits = new Set()

	constructor(auth, store, dataManager, storageManager) {
		super();
		this.auth = auth
		this.store = store
		this.dataManager = dataManager
		this.storageManager = storageManager
	}

	logAuthTrace(event, details = {}) {
		try {
			this.authTraceRun ||= `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
			this.authTraceSequence = (this.authTraceSequence || 0) + 1
			let state = this.store.state.store
			let profile = state.userProfile
			console.log('[AuthTrace]', JSON.stringify({
				run: this.authTraceRun, sequence: this.authTraceSequence, at: new Date().toISOString(), event,
				sessionVersion: this.sessionVersion, operation: this.activeOperation?.kind || null,
				firebaseUid: this.auth.currentUser?.uid || null,
				storeUid: state.currentUser?.uid || null, profileId: profile?.id || null,
				cachedProfileId: this.userProfile?.id || null,
				dataUserId: this.dataManager.currentUserId || null, dataOwnerId: this.dataManager.ownerId || null,
				storageUserId: this.storageManager.currentUserId || null, storageOwnerId: this.storageManager.ownerId || null,
				legacyOwnerId: this.server?.paths?.coachId || null,
				profileOwnerId: profile?.currentOwnerId || null, role: profile?.role || null,
				portalMemberId: profile?.currentPortalRef?.userId || null,
				ownerProfileId: state.ownerProfile?.id || null, coachProfileId: state.coachProfile?.id || null,
				isTokenSet: state.isTokenSet, claimsListening: !!this.isListeningForClaimsChange,
				...details,
			}))
		} catch(error) {
			// Diagnostics must not interrupt authentication.
		}
		return this.authTraceSequence
	}

	isLoggedIn(){
		let state = this.store.state.store.authState
		let retainsSession = state === AuthStateEnum.BUSY
			&& [AuthOperationEnum.SELECT, AuthOperationEnum.REFRESH].includes(this.activeOperation?.kind)
		return (state === AuthStateEnum.READY || retainsSession)
			&& !!this.auth.currentUser && this.getUserProfile()?.id === this.auth.currentUser.uid
	}

	setAuthStatus(status, error = null){
		this.store.commit('setAuthStatus', {status, error})
		this.logAuthTrace('auth.state', {status, code: error?.code, message: error?.message})
	}

	runAccountOperation(kind, work){
		if(this.activeOperation){
			this.logAuthTrace('operation.rejected_busy', {requestedOperation: kind})
			return Promise.reject(Object.assign(new Error('Please wait for the current account operation to finish.'), {code: 'auth/busy'}))
		}
		if(kind === AuthOperationEnum.LOGIN || kind === AuthOperationEnum.INITIALIZE) this.resetSession()
		let previousState = this.store.state.store.authState
		let version = this.sessionVersion
		let operation = {kind, user: null, promise: null, canRetainAccount: true}
		let startedAt = Date.now()
		this.activeOperation = operation
		this.logAuthTrace('operation.start', {kind})
		this.setAuthStatus(AuthStateEnum.BUSY)
		operation.promise = Promise.resolve().then(() => work(version)).then(result => {
			if(version === this.sessionVersion){
				this.setAuthStatus(this.auth.currentUser ? AuthStateEnum.READY : AuthStateEnum.SIGNED_OUT)
			}
			return result
		}).catch(error => {
			this.logAuthTrace('operation.failed', {kind, code: error?.code, message: error?.message})
			if(version === this.sessionVersion){
				let retainsAccount = previousState === AuthStateEnum.READY
					&& operation.canRetainAccount
					&& [AuthOperationEnum.SELECT, AuthOperationEnum.REFRESH].includes(kind)
					&& this.store.state.store.currentUser?.uid === this.auth.currentUser?.uid
				if(!retainsAccount) this.resetSession()
				let status = retainsAccount ? AuthStateEnum.READY
					: this.auth.currentUser ? AuthStateEnum.ERROR : AuthStateEnum.SIGNED_OUT
				this.setAuthStatus(status, status === AuthStateEnum.ERROR ? error : null)
			}
			throw error
		}).finally(() => {
			this.logAuthTrace('operation.finished', {kind, durationMs: Date.now() - startedAt,
				status: this.store.state.store.authState, sessionChanged: version !== this.sessionVersion})
			this.activeOperation = null
			this.notifyAuthCallbacks()
		})
		return operation.promise
	}

	assertCurrentUser(user, version){
		if(version !== this.sessionVersion || !user || this.auth.currentUser?.uid !== user.uid){
			throw Object.assign(new Error('Authentication changed. Please sign in again.'), {code: 'auth/session-changed'})
		}
	}

	resetSession(){
		this.sessionVersion++
		for(let cancel of this.sessionWaits) cancel()
		this.claimsUnsubscribe?.()
		this.claimsUnsubscribe = null
		clearTimeout(this.claimsRetryTimer)
		this.claimsRetryTimer = null
		this.isListeningForClaimsChange = false
		this.clearAccountContext()
	}

	getUserProfile(){
		let userProfile = this.store.state.store.userProfile;
		console.log("AuthManager.getUserProfile = ", userProfile)
		return userProfile;
	}
	getCoachProfile(){
		let coachProfile = this.store.state.store.coachProfile;
		console.log("AuthManager.getCoachProfile = ", coachProfile)
		return coachProfile;
	}
	getOwnerProfile(){
		let ownerProfile = this.store.state.store.ownerProfile;
		console.log("AuthManager.getOwnerProfile = ", ownerProfile)
		return ownerProfile;
	}

	getCurrentUser(){
		let currentUser = this.store.state.store.currentUser;
		console.log("AuthManager.getCurrentUser = ", currentUser)
		return currentUser;
	}

	isQwoachV2App(){
		let ownerProfile = this.getOwnerProfile()
		return ownerProfile && ownerProfile.appVersion === 2
	}

	getUserRole(){
		return this.isLoggedIn() ? this.getUserProfile().role : null;
	}

	isOwner(){
		let userProfile = this.getUserProfile()
		if(userProfile){
			return userProfile.isOwner()
		}
		return false
	}
	isCoach(){
		return this.getUserRole() === RoleEnum.COACH
	}

	isClient(){
		return this.getUserRole() === RoleEnum.CLIENT
	}

	getCoachId(){
		let ownerProfile = this.getOwnerProfile()
		return ownerProfile ? ownerProfile.id : null
	}

	getOwnerId(){
		let ownerProfile = this.getOwnerProfile()
		return ownerProfile ? ownerProfile.id : null
	}



	logout(){
		let loginPathName = this.getLoginPathName()
		return this.runAccountOperation(AuthOperationEnum.LOGOUT, async () => {
			this.resetSession()
			try {
				await this.auth.signOut()
				this.setAuthStatus(AuthStateEnum.SIGNED_OUT)
				return loginPathName
			} catch(error){
				this.setAuthStatus(AuthStateEnum.ERROR, error)
				throw error
			}
		})
	}

	getLoginPathName(){
		if(this.isCoach()){
			return "LoginCoach"
		}
		return "Login"
	}

	async clientSignup(password, userProfile){
		console.log("AuthManager.clientSignup start")
		if(!userProfile.email){
			throw new Error("clientSignup: missing required filed userProfile.email")
		}
		userProfile = Object.assign({}, userProfile)
		userProfile.password = password
		userProfile = await this.dataManager.apiPostCreateClient(userProfile)
		return await this.login(userProfile.email, password, true)
	}

	refreshUserToken(){
		return this.requestProfileRefresh(true)
	}

	requestProfileRefresh(refreshToken = false, checkClaimsOnly = false){
		if(!this.auth.currentUser) return Promise.resolve(null)
		let coalesced = !!this.refresh && this.refresh.version === this.sessionVersion
		if(!this.refresh || this.refresh.version !== this.sessionVersion){
			let refresh = {user: this.auth.currentUser, version: this.sessionVersion,
				requested: false, refreshToken: false, reloadProfile: false, promise: null}
			this.refresh = refresh
			refresh.promise = Promise.resolve().then(() => this.refreshAccount(refresh))
		}
		this.refresh.requested = true
		this.refresh.refreshToken ||= refreshToken
		this.refresh.reloadProfile ||= !checkClaimsOnly
		this.logAuthTrace('refresh.requested', {refreshToken, checkClaimsOnly, coalesced,
			reloadProfile: this.refresh.reloadProfile})
		return this.refresh.promise
	}

	async refreshAccount(refresh){
		let userProfile
		try {
			while(refresh.requested){
				await this.waitUntilReady()
				this.assertCurrentUser(refresh.user, refresh.version)
				// Another caller can acquire the lock after readiness was announced.
				if(this.activeOperation) continue
				refresh.requested = false
				let refreshToken = refresh.refreshToken
				let reloadProfile = refresh.reloadProfile
				refresh.refreshToken = false
				refresh.reloadProfile = false
				let startedAt = Date.now()
				this.logAuthTrace('refresh.pass_start', {refreshToken, reloadProfile})
				try {
					userProfile = await this.refreshAccountPass(refresh, {refreshToken, reloadProfile})
					this.logAuthTrace('refresh.pass_complete', {durationMs: Date.now() - startedAt,
						hasProfile: !!userProfile, anotherPassRequested: refresh.requested})
					if(!userProfile) return null
				} catch(error){
					this.logAuthTrace('refresh.pass_failed', {durationMs: Date.now() - startedAt,
						code: error?.code, message: error?.message, anotherPassRequested: refresh.requested})
					if(!refresh.requested || refresh.version !== this.sessionVersion) throw error
					this.assertCurrentUser(refresh.user, refresh.version)
				}
			}
			return userProfile
		} finally {
			if(this.refresh === refresh) this.refresh = null
		}
	}

	async refreshAccountPass(refresh, {refreshToken, reloadProfile}){
		if(!reloadProfile){
			if(this.store.state.store.authState !== AuthStateEnum.READY){
				this.logAuthTrace('claims.check_skipped', {status: this.store.state.store.authState})
				return null
			}
			let startedAt = Date.now()
			this.logAuthTrace('claims.token_check_start')
			let token = await refresh.user.getIdTokenResult(true)
			this.logAuthTrace('claims.token_check_complete', {durationMs: Date.now() - startedAt})
			await this.waitUntilReady()
			this.assertCurrentUser(refresh.user, refresh.version)
			if(this.store.state.store.authState !== AuthStateEnum.READY) return null
			if(this.getUserProfile().accessRules.matchesCustomClaims(token.claims)){
				this.logAuthTrace('claims.unchanged', {reloadProfile: false})
				return this.getUserProfile()
			}
			refreshToken = false
			this.logAuthTrace('claims.changed', {reloadProfile: true})
		}
		if(this.activeOperation){
			this.logAuthTrace('refresh.deferred', {reason: 'account-operation-active'})
			refresh.requested = true
			refresh.reloadProfile = true
			refresh.refreshToken ||= refreshToken
			return this.getUserProfile()
		}
		return await this.runAccountOperation(AuthOperationEnum.REFRESH, async () => {
			if(refreshToken){
				this.logAuthTrace('refresh.token_start')
				await refresh.user.getIdToken(true)
				this.logAuthTrace('refresh.token_complete')
			}
			this.assertCurrentUser(refresh.user, refresh.version)
			this.logAuthTrace('refresh.profile_read_start')
			let profile = await this.dataManager.getUserProfile(refresh.user.uid)
			this.logAuthTrace('refresh.profile_read_complete', {loadedProfileId: profile?.id || null})
			return await this.loadRefreshedAccount(profile, this.getAccountContext(), refresh.version)
		})
	}

	async getUserByEmail(email){
		return await this.auth.getUserByEmail(email)
	}

	async waitForCustomClaimsFor(ownerId){
		return await this.waitForSessionSnapshot(
			callback => this.dataManager.onSnapshotCustomClaimsDoc(callback),
			async () => {
				await this.refreshUserToken()
				if(this.getUserProfile()?.hasAccessRulesFor(ownerId)) return true
			})
	}

	async startListenForClaimsChange(){
		console.log("AuthManager.startListenForClaimsChange")
		if(this.isListeningForClaimsChange){
			this.logAuthTrace('claims.registration_skipped')
			return
		}
		let version = this.sessionVersion
		let listenerId = this.logAuthTrace('claims.register', {listenerUid: this.dataManager.currentUserId})
		let listenerUid = this.dataManager.currentUserId
		this.isListeningForClaimsChange = true
		let stop = await this.dataManager.onSnapshotCustomClaimsDoc((doc, error) => {
			if(version !== this.sessionVersion) return
			this.logAuthTrace('claims.callback', {listenerId, listenerUid, code: error?.code, message: error?.message, claimsChangedTs: doc?.claimsChangedTs})
			console.log("AuthManager.onSnapshotCustomClaimsDoc = ", doc, error)
			if(error){
				logError(error)
				this.isListeningForClaimsChange = false
				if(error.code !== "permission-denied"){
					this.logAuthTrace('claims.retry_scheduled', {listenerId, listenerUid})
					this.claimsRetryTimer = setTimeout(() => {
						this.logAuthTrace('claims.retry_fired', {listenerId, listenerUid})
						if(version === this.sessionVersion) this.startListenForClaimsChange().catch(logError)
					}, 10000)
				}
			} else {
				this.requestProfileRefresh(true, true).catch(logError)
			}
		})
		if(version === this.sessionVersion) this.claimsUnsubscribe = stop
		else stop()

	}

	async memberSignup(password, userProfile){
		console.log("AuthManager.memberSignup start")
		if(!userProfile.email){
			throw new Error("memberSignup: missing required filed userProfile.email")
		}
		userProfile = Object.assign({}, userProfile)
		userProfile.password = password
		userProfile = await this.dataManager.apiPostCreateTeamMemberAccount(userProfile)
		return await this.login(userProfile.email, password, true)
	}


	login(email, password, isUserCreated){
		return this.runAccountOperation(AuthOperationEnum.LOGIN, async version => {
			let {user} = await this.auth.signInWithEmailAndPassword(email, password)
			return await this.initializeUser(user, isUserCreated, version)
		})
	}

	onUserLoggedIn(user, isUserCreated){
		if(this.activeOperation){
			if([AuthOperationEnum.LOGIN, AuthOperationEnum.INITIALIZE].includes(this.activeOperation.kind)) return this.activeOperation.promise
			return Promise.reject(Object.assign(new Error('Account loading is in progress.'), {code: 'auth/busy'}))
		}
		if(this.isLoggedIn()) return Promise.resolve(this.getUserProfile())
		return this.runAccountOperation(AuthOperationEnum.INITIALIZE,
			version => this.initializeUser(user, isUserCreated, version))
	}

	retryInitialization(){
		if(this.activeOperation) return this.activeOperation.promise
		let user = this.auth.currentUser
		if(!user){
			this.resetSession()
			this.setAuthStatus(AuthStateEnum.SIGNED_OUT)
			this.notifyAuthCallbacks()
			return Promise.resolve(null)
		}
		return this.onUserLoggedIn(user)
	}


	async logUserLoggedIn(userProfile, isUserCreated){
		this.analyticManager.setUserId(userProfile.id, isUserCreated)
		this.analyticManager.setUserEmail(userProfile.email)
		await this.analyticManager.setUserProps({
			role: userProfile.role
		})
	}


	async initializeUser(user, isUserCreated, version){
		this.assertCurrentUser(user, version)
		this.activeOperation.user = user
		let startedAt = Date.now()
		this.logAuthTrace('startup.profile_subscription_start')
		let [userProfile, userSubscription] = await Promise.all([
			this.dataManager.getUserProfile(user.uid),
			this.dataManager.apiGetUserSubscription({userId: user.uid}),
		])
		this.logAuthTrace('startup.profile_subscription_complete', {durationMs: Date.now() - startedAt,
			loadedProfileId: userProfile?.id || null})
		this.assertCurrentUser(user, version)
		if(userProfile?.id !== user.uid) throw new Error('The account profile does not match the authenticated user.')
		this.dataManager.currentUserId = user.uid
		this.storageManager.currentUserId = user.uid
		this.logAuthTrace('startup.timezone_start')
		await this.ensureTimeZone(userProfile)
		this.logAuthTrace('startup.timezone_complete')
		this.assertCurrentUser(user, version)
		userProfile = await this.loadDefaultAccount(userProfile, {currentUser: user, userSubscription}, version)
		if(!userProfile) return null
		await this.startListenForClaimsChange()
		this.assertCurrentUser(user, version)
		this.userProfile = userProfile
		this.logUserLoggedIn(userProfile, isUserCreated).catch(logError)
		return userProfile
	}

	async ensureTimeZone(userProfile){
		let timezoneId = userTimezone()
		console.log("ensureTimeZone: timezoneId = ", timezoneId, ", userProfile = ", userProfile)
		if(userProfile.getTimeZoneId() !== timezoneId){
			let timezoneName = userTimezoneName(timezoneId)
			await this.dataManager.apiPostSetUserTimezone({userProfile, timezoneId, timezoneName})
		}
	}

	setDefaultPortal(userProfile){
		return this.runAccountOperation(AuthOperationEnum.SELECT,
			version => this.loadDefaultAccount(userProfile, this.getAccountContext(), version))
	}

	async loadDefaultAccount(userProfile, account, version){
		this.assertCurrentUser(account.currentUser, version)
		let selection = this.portalManager.getDefaultPortalSelection(userProfile)
		this.logAuthTrace('account.selected', {selectedOwnerId: selection.userProfile.getOwnerId(),
			selectedRole: selection.userProfile.role, shouldSave: selection.shouldSave})
		let context = await this.loadPortalWithRecovery(selection.userProfile, account, version)
		if(!context) return null
		this.assertCurrentUser(account.currentUser, version)
		if(selection.shouldSave && context.userProfile === selection.userProfile){
			this.logAuthTrace('account.selection_save_start')
			await this.dataManager.updateUserProfile(context.userProfile)
			this.logAuthTrace('account.selection_save_complete')
		}
		this.assertCurrentUser(account.currentUser, version)
		this.applyAccountContext({...account, ...context})
		return context.userProfile
	}

	async loadRefreshedAccount(profile, account, version){
		this.assertCurrentUser(account.currentUser, version)
		if(profile?.id !== account.currentUser.uid){
			this.activeOperation.canRetainAccount = false
			throw new Error('The account profile does not match the authenticated user.')
		}
		let currentRef = account.userProfile.currentPortalRef
		let refs = this.portalManager.getAvailablePortalRefs(profile)
		let ref = refs.find(item => item.ownerId === currentRef?.ownerId && item.role === currentRef?.role)
		if(!ref){
			this.logAuthTrace('account.selection_no_longer_available', {selectedOwnerId: currentRef?.ownerId,
				selectedRole: currentRef?.role})
			this.activeOperation.canRetainAccount = false
			return await this.loadDefaultAccount(profile, account, version)
		}
		let userProfile = classToClass(profile)
		userProfile.setPortalRef(ref)
		let context = await this.loadAccountPortal(userProfile, account, version, true)
		return context?.userProfile || null
	}

	async loadPortalWithRecovery(userProfile, account, version){
		let startedAt = Date.now()
		let selectedOwnerId = userProfile.getOwnerId()
		this.logAuthTrace('account.load_start', {selectedOwnerId, selectedRole: userProfile.role})
		try {
			let context = await this.portalManager.loadPortal(userProfile)
			this.logAuthTrace('account.load_complete', {selectedOwnerId, durationMs: Date.now() - startedAt})
			return context
		} catch(error){
			this.logAuthTrace('account.load_failed', {selectedOwnerId, durationMs: Date.now() - startedAt,
				code: error?.code, message: error?.message})
			let canRecover = [AuthOperationEnum.LOGIN, AuthOperationEnum.INITIALIZE,
				AuthOperationEnum.REFRESH].includes(this.activeOperation?.kind)
			if(!canRecover || !this.isAccountPermissionDenied(error)) throw error
			let result = await this.recoverAccountAccess(userProfile.currentPortalRef, account, version)
			this.logAuthTrace('recovery.complete', {hasContext: !!result.context,
				code: result.error?.code, message: result.error?.message})
			if(result.error && version === this.sessionVersion){
				logError(result.error)
				this.resetSession()
				this.setAuthStatus(this.auth.currentUser ? AuthStateEnum.ERROR : AuthStateEnum.SIGNED_OUT, result.error)
			}
			return result.context
		}
	}

	isAccountPermissionDenied(error){
		return ['permission-denied', 'firestore/permission-denied', 'functions/permission-denied'].includes(error?.code)
	}

	async recoverAccountAccess(selectedRef, account, version){
		this.activeOperation.canRetainAccount = false
		this.logAuthTrace('recovery.start', {selectedOwnerId: selectedRef?.ownerId, selectedRole: selectedRef?.role})
		try {
			this.assertCurrentUser(account.currentUser, version)
			this.logAuthTrace('recovery.token_start')
			await account.currentUser.getIdToken(true)
			this.logAuthTrace('recovery.token_complete')
			this.assertCurrentUser(account.currentUser, version)
			this.logAuthTrace('recovery.profile_read_start')
			let profile = await this.dataManager.getUserProfile(account.currentUser.uid)
			this.logAuthTrace('recovery.profile_read_complete', {loadedProfileId: profile?.id || null})
			this.assertCurrentUser(account.currentUser, version)
			if(profile?.id !== account.currentUser.uid){
				return {context: null, error: {code: 'auth/profile-mismatch',
					message: 'The account profile does not match the authenticated user.'}}
			}
			return await this.loadRecoveryCandidates(profile, selectedRef, account, version)
		} catch(error){
			return {context: null, error}
		}
	}

	async loadRecoveryCandidates(profile, selectedRef, account, version){
		let refs = this.portalManager.getAvailablePortalRefs(profile)
		let shared = refs.filter(ref => ref.ownerId !== profile.id)
		let selected = shared.find(ref => ref.ownerId === selectedRef?.ownerId && ref.role === selectedRef?.role)
		let own = refs.filter(ref => ref.ownerId === profile.id)
		let candidates = [...new Set([selected, ...shared, ...own].filter(Boolean))]
		this.logAuthTrace('recovery.candidates', {count: candidates.length, selectedStillAvailable: !!selected,
			sharedCount: shared.length, ownCount: own.length})
		for(let ref of candidates){
			let candidate = classToClass(profile)
			candidate.setPortalRef(ref)
			let context
			let startedAt = Date.now()
			this.logAuthTrace('recovery.candidate_start', {candidateOwnerId: ref.ownerId,
				candidateRole: ref.role, isOwnAccount: ref.ownerId === profile.id})
			try {
				context = await this.portalManager.loadPortal(candidate)
			} catch(error){
				this.logAuthTrace('recovery.candidate_failed', {candidateOwnerId: ref.ownerId,
					candidateRole: ref.role, durationMs: Date.now() - startedAt,
					code: error?.code, message: error?.message})
				this.assertCurrentUser(account.currentUser, version)
				if(!this.isAccountPermissionDenied(error)) return {context: null, error}
				continue
			}
			this.assertCurrentUser(account.currentUser, version)
			this.logAuthTrace('recovery.selection_save_start', {candidateOwnerId: ref.ownerId})
			await this.dataManager.updateUserProfile(context.userProfile)
			this.logAuthTrace('recovery.candidate_complete', {candidateOwnerId: ref.ownerId,
				candidateRole: ref.role, durationMs: Date.now() - startedAt})
			this.assertCurrentUser(account.currentUser, version)
			return {context, error: null}
		}
		this.logAuthTrace('recovery.exhausted')
		return {context: null, error: {code: 'auth/no-accessible-account',
			message: 'No accessible accounts are available. Please retry or sign out.'}}
	}

	fetchAndSyncUserProfile(){
		return this.requestProfileRefresh()
	}

	async updateUserProfile(userProfile, options = {}){
		console.trace("AuthManager.updateUserProfile: userProfile = ", userProfile, ", options = ", options)
		let context = {...this.getAccountContext(), userProfile}
		if(userProfile.isOwner()){
			if(userProfile.isCoach()) context.coachProfile = userProfile
			context.ownerProfile = userProfile
		}
		this.applyAccountContext(context)
		if(!options.localOnly) {
			await this.dataManager.setUserProfile(userProfile)
		}
	}

	setUserSubscription(userSubscription){
		this.store.commit('setUserSubscription', userSubscription)
	}

	selectAccountForOwner(ownerId, roles){
		return this.runAccountOperation(AuthOperationEnum.SELECT, async version => {
			let account = this.getAccountContext()
			this.assertCurrentUser(account.currentUser, version)
			let refs = this.portalManager.getAvailablePortalRefs(account.userProfile)
			let ref = refs.find(item => item.ownerId === ownerId && roles.includes(item.role))
			if(!ref) return null
			let profile = classToClass(account.userProfile)
			profile.setPortalRef(ref)
			let context = await this.loadAccountPortal(profile, account, version)
			return context.userProfile
		})
	}

	setCurrentCoachProfile(userProfile){
		return this.runAccountOperation(AuthOperationEnum.SELECT, async version => {
			let account = this.getAccountContext()
			this.assertCurrentUser(account.currentUser, version)
			let context = await this.loadAccountPortal(userProfile, account, version)
			return context.coachProfile
		})
	}

	async loadAccountPortal(userProfile, account, version, recoverAccess = false){
		let context = recoverAccess
			? await this.loadPortalWithRecovery(userProfile, account, version)
			: await this.portalManager.loadPortal(userProfile)
		if(!context) return null
		this.assertCurrentUser(account.currentUser, version)
		this.applyAccountContext({...account, ...context})
		return context
	}

	getAccountContext(){
		let {currentUser, userProfile, userSubscription, coachProfile, ownerProfile, ownerSubscription} = this.store.state.store
		return {currentUser, userProfile, userSubscription, coachProfile, ownerProfile, ownerSubscription}
	}

	applyAccountContext(context){
		let uid = context.currentUser?.uid || null
		let ownerId = context.userProfile?.getOwnerId?.() || null
		this.dataManager.currentUserId = uid
		this.storageManager.currentUserId = uid
		this.dataManager.ownerId = ownerId
		this.storageManager.ownerId = ownerId
		this.server.fbStorage.coachId = ownerId
		this.server.paths.coachId = ownerId
		this.store.commit('setAccountContext', context)
		this.logAuthTrace('account.context_applied')
		if(context.currentUser) setClientSessionMarker()
		else clearClientSessionMarker()
	}

	clearAccountContext(){
		this.userProfile = null
		this.applyAccountContext({currentUser: null, userProfile: {}, userSubscription: {},
			coachProfile: null, ownerProfile: null, ownerSubscription: null})
		this.portalManager.portalRefCache = {}
	}


	async waitForUserSubscription(options = {}){
		let {paymentStartTs = 0, userId = this.getCoachId(),
			predicate = subscription => subscription.isStarted() || subscription.isPending()} = options
		return await this.waitForSessionSnapshot(
			callback => this.dataManager.onSnapshotUserSubscription(userId, callback),
			subscription => {
				if(subscription && subscription.updatedAt >= paymentStartTs && predicate(subscription)){
					this.setUserSubscription(subscription)
					return subscription
				}
			})
	}

	waitForSessionSnapshot(register, readResult){
		let user = this.auth.currentUser
		let version = this.sessionVersion
		this.assertCurrentUser(user, version)
		return new Promise((resolve, reject) => {
			let settled = false
			let unsubscribe
			let finish = (error, result) => {
				if(settled) return
				settled = true
				this.sessionWaits.delete(cancel)
				unsubscribe?.()
				if(error) reject(error)
				else resolve(result)
			}
			let cancel = () => finish(Object.assign(new Error('Signed out while waiting for account data.'), {code: 'auth/session-changed'}))
			this.sessionWaits.add(cancel)
			Promise.resolve().then(() => {
				if(settled) return
				return register(async (value, error) => {
					if(settled) return
					try {
						this.assertCurrentUser(user, version)
						if(error) throw error
						let result = await readResult(value)
						this.assertCurrentUser(user, version)
						if(result !== undefined) finish(null, result)
					} catch(error){ finish(error) }
				})
			}).then(stop => {
				unsubscribe = stop
				if(settled) unsubscribe?.()
			}).catch(error => finish(error))
		})
	}


	notifyAuthCallback(callback){
		let state = this.store.state.store
		if(this.activeOperation || state.authState === AuthStateEnum.STARTING) return
		Promise.resolve().then(() => {
			if(this.authCallbacks.has(callback) && !this.activeOperation){
				return callback(state.authState === AuthStateEnum.READY ? this.getUserProfile() : null, state.authError)
			}
		}).catch(logError)
	}

	notifyAuthCallbacks(){
		for(let callback of this.authCallbacks) this.notifyAuthCallback(callback)
	}

	startListenForLoginStateChange(callback = () => {}){
		this.authCallbacks.add(callback)
		if(!this.authObserver){
			this.authObserver = this.auth.onAuthStateChanged(
				user => this.handleAuthState(user).catch(logError),
				error => {
					this.resetSession()
					this.setAuthStatus(AuthStateEnum.ERROR, error)
					this.notifyAuthCallbacks()
				})
		}
		if(!this.activeOperation && this.store.state.store.authState !== AuthStateEnum.STARTING){
			Promise.resolve().then(() => {
				if(this.authCallbacks.has(callback)) this.notifyAuthCallback(callback)
			})
		}
		return () => this.authCallbacks.delete(callback)
	}

	async handleAuthState(user){
		if(user?.uid !== this.auth.currentUser?.uid) return
		let operation = this.activeOperation
		if(operation){
			if(operation.kind === AuthOperationEnum.LOGOUT) return
			if(operation.kind === AuthOperationEnum.LOGIN && !operation.user) return
			let expectedUser = operation.user || this.store.state.store.currentUser
			if(expectedUser?.uid === user?.uid) return
			this.resetSession()
			this.setAuthStatus(user ? AuthStateEnum.STARTING : AuthStateEnum.SIGNED_OUT)
			await operation.promise.catch(() => {})
			if(user?.uid !== this.auth.currentUser?.uid) return
		}
		if(user){
			if(this.isLoggedIn()) return
			if(this.store.state.store.authState === AuthStateEnum.ERROR) return
			await this.onUserLoggedIn(user).catch(() => {})
		} else {
			if(this.store.state.store.authState === AuthStateEnum.SIGNED_OUT) return
			this.resetSession()
			this.setAuthStatus(AuthStateEnum.SIGNED_OUT)
			this.notifyAuthCallbacks()
		}
	}

	waitUntilReady(){
		return new Promise(resolve => {
			let unsubscribe = this.startListenForLoginStateChange(() => {
				unsubscribe()
				resolve()
			})
		})
	}

}
